import { Link } from "react-router-dom";
import useDepartmentDoctors from "../hooks/useDepartmentDoctors";
import DoctorCard from "./DoctorCard";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Phone, Mail, MapPin, CheckCircle2, Users, Award, Clock, Heart, Shield } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion";
import SEO from "../pages/SEO"
import { useAppointment } from "../context/AppointmentContext"
export default function AnaesthesiologyPage() {
  const { open: openAppointment } = useAppointment();
  const doctors = useDepartmentDoctors("Anaesthesiology");




  const subspecialties = [
    {
      title: "General Anaesthesia",
      description: "For major and complex surgeries with complete sedation and monitoring.",
      image: "/GeneralAnaesthesia.jpg",
    },
    {
      title: "Regional Anaesthesia",
      description: "Spinal, epidural, and nerve blocks for targeted pain relief.",
      image: "/RegionalAnaesthesia.jpg",
    },
    {
      title: "Cardiac Anaesthesia",
      description: "Specialized care for heart and thoracic surgeries.",
      image: "/CardiacAnaesthesia.jpg",
    },
    {
      title: "Neuro-Anaesthesia",
      description: "Anesthesia for brain and spine surgeries with precision monitoring.",
      image: "/Neuro-Anaesthesia.jpg",
    },
    {
      title: "Pediatric Anaesthesia",
      description: "Safe anesthesia care for infants and children with specialized protocols.",
      image: "/PediatricAnaesthesia.jpg",
    },
    {
      title: "Critical Care & Perioperative Medicine",
      description: "ICU support, sedation and advanced monitoring for critically ill patients.",
      image: "/CriticalCare&PerioperativeMedicine.jpg",
    },
    {
      title: "Pain Management Services",
      description: "Chronic and acute pain interventions for improved quality of life.",
      image: "/PainManagementServices.jpg",
    },
  ];

  const treatments = [
    "Preoperative Evaluation & Risk Assessment",
    "General, Spinal & Epidural Anaesthesia",
    "Peripheral Nerve Blocks for Orthopedic & Trauma Surgeries",
    "Sedation for Endoscopy, Minor Procedures & Diagnostics",
    "Intraoperative Monitoring & Life Support",
    "Postoperative Pain Management",
    "Advanced Airway & Ventilation Management",
    "Anaesthesia for Emergency & Trauma Surgeries",
  ];

  const whyChoose = [
    { icon: Users, title: "Expert Anesthesiologists", description: "Experienced anesthesiologists with subspecialty expertise" },
    { icon: Award, title: "Modern Equipment", description: "Modern anesthesia workstations and advanced monitoring systems" },
    { icon: Shield, title: "Strict Safety Protocols", description: "Strict safety protocols and infection-controlled OT environment" },
    { icon: Heart, title: "Personalized Care", description: "Personalized anesthesia plans based on patient profile and needs" },
    { icon: Clock, title: "24x7 Coverage", description: "24x7 anesthesia coverage for emergencies and critical surgeries" }
  ];

  const articles = [
    {
      title: "Understanding Your Anaesthesia Options Before Surgery",
      excerpt: "Learn about different types of anesthesia and how the anesthesiologist determines the best option for your procedure.",
    },
    {
      title: "How Regional Anaesthesia Helps Reduce Pain After Surgery",
      excerpt: "Discover the benefits of regional anesthesia techniques in managing postoperative pain and speeding recovery.",
    },
    {
      title: "The Role of Anaesthesiologists in the ICU",
      excerpt: "Understand how anesthesiologists provide critical care support and sedation management in intensive care units.",
    },
    {
      title: "What to Expect During Postoperative Recovery",
      excerpt: "Essential information about the recovery process and how anesthesia care continues after your surgery.",
    },
  ];

  const faqs = [
    {
      question: "Is anesthesia safe?",
      answer:
        "Yes, when administered by trained anesthesiologists with modern monitoring equipment, anesthesia is extremely safe. Our team follows strict safety protocols and continuously monitors vital signs throughout the procedure to ensure patient safety.",
    },
    {
      question: "Will I feel pain during surgery?",
      answer:
        "No, anesthesia ensures a pain-free surgical experience with complete comfort. The type of anesthesia used depends on your procedure, but our goal is always to eliminate pain and discomfort during the operation.",
    },
    {
      question: "Do you provide pediatric anesthesia?",
      answer:
        "Yes, we have specialized pediatric anesthesiologists who ensure safe care for infants and children. They use age-appropriate techniques and medications, with careful monitoring tailored to pediatric patients' unique needs.",
    },
  ];
  const schema = [
  {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    "name": "MAMS Hospitals Anaesthesiology Department",
    "url": "https://mamshospitals.com/anaesthesiology",
    "logo": "https://mamshospitals.com/MAMS-Logo.png",
    "image": "https://mamshospitals.com/Anaesthesiology.jpg",
    "description": "MAMS Hospitals provides advanced anaesthesiology services in Hyderabad including general anesthesia, regional anesthesia, pediatric anesthesia, cardiac anesthesia, pain management, and perioperative critical care.",
    "medicalSpecialty": "Anesthesiology",
    "telephone": "+91-7399999981",
    "priceRange": "$$",

    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bachupally",
      "addressLocality": "Hyderabad",
      "addressRegion": "Telangana",
      "postalCode": "500090",
      "addressCountry": "IN"
    },

    "availableService": [
      {
        "@type": "MedicalProcedure",
        "name": "General Anaesthesia"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Regional & Spinal Anaesthesia"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Pediatric Anaesthesia"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Cardiac & Neuro Anaesthesia"
      },
      {
        "@type": "MedicalProcedure",
        "name": "Pain Management & ICU Support"
      }
    ],

    "sameAs": [
      "https://www.facebook.com/MAMSHospitals",
      "https://www.instagram.com/mams_hospitals"
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Is anesthesia safe?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, anesthesia is extremely safe when administered by experienced anesthesiologists using advanced monitoring and safety protocols."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide pediatric anesthesia services?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, MAMS Hospitals provides specialized pediatric anesthesia care for infants and children."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide pain management services?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, we provide chronic pain management, nerve blocks, perioperative pain control, and postoperative pain relief services."
        }
      }
    ]
  },

  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Anaesthesiology Department - MAMS Hospitals",
    "url": "https://mamshospitals.com/anaesthesiology",
    "description": "Best anaesthesiology department in Hyderabad offering general anesthesia, regional anesthesia, pediatric anesthesia, cardiac anesthesia, pain management, and perioperative critical care.",
    "keywords": [
      "Best Anaesthesiologist Hyderabad",
      "Anaesthesia Hospital Hyderabad",
      "General Anaesthesia Hyderabad",
      "Pediatric Anaesthesia Hyderabad",
      "Pain Management Hyderabad",
      "Regional Anaesthesia Hyderabad",
      "Anaesthesia Hospital Bachupally"
    ]
  }
];

  return (
    <>
  <SEO
      title="Best Anaesthesiology Department in Hyderabad | Safe Anesthesia Care | MAMS Hospitals"
      description="MAMS Hospitals offers advanced anaesthesiology services in Hyderabad including general anesthesia, regional anesthesia, pediatric anesthesia, cardiac anesthesia, pain management, and perioperative critical care."
      keywords="best anaesthesiologist Hyderabad,
  anaesthesia hospital Hyderabad,
  general anaesthesia Hyderabad,
  regional anaesthesia Hyderabad,
  spinal anaesthesia Hyderabad,
  pediatric anaesthesia Hyderabad,
  cardiac anaesthesia Hyderabad,
  pain management Hyderabad,
  perioperative care Hyderabad,
  critical care anaesthesia Hyderabad,
  anesthesia specialist Hyderabad,
  anaesthesia hospital Bachupally,
  MAMS Hospitals Anaesthesiology"
      schema={schema}
    />
    <div className="flex flex-col min-h-screen">

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-blue-50 py-12 md:py-24">
        <div className="container mx-auto px-0 max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">

            <div>
              <div className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                Best Anaesthesiology Department in Hyderabad
              </div>

              <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
                Safe & Precise Anesthesia Care for Every Surgery
              </h1>

              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                MAMS Hospitals is home to one of the Best Anaesthesiology Departments in Hyderabad, providing safe,
                precise, and patient-centered anesthesia care for all surgical specialties. Your safety is our priority.
              </p>

             <div className="flex flex-col sm:flex-row gap-4">

                <Button className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-8" size="lg" onClick={openAppointment}>
                    Book Appointment
                  </Button>

                <Link to="tel:+91-7399999981">
                  <Button
                    className="border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full px-8"
                    variant="outline"
                    size="lg"
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Call Us
                  </Button>
                </Link>

              </div>
            </div>

            <div className="relative">
              <div className="absolute -top-4 -right-4 bg-teal-600 text-white p-6 rounded-2xl shadow-xl z-10">
                <div className="text-3xl font-bold">24/7</div>
                <div className="text-sm">Anesthesia Coverage</div>
              </div>

              <img
                src="/Anaesthesiology.jpg"
                alt="Anaesthesiology at MAMS Hospital"
                className="rounded-2xl shadow-2xl w-full"
              canonicalUrl="https://mamshospitals.com/departments/anaesthesiology"
      />
            </div>

          </div>
        </div>
      </section>

      {/* Sub-Specialties */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-0 max-w-6xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Sub-Specialities in Anaesthesiology</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">Comprehensive anesthesia expertise across multiple specializations</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {subspecialties.map((specialty, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 border-gray-200 hover:border-teal-500">
                <CardContent className="p-0">

                  <div className="relative h-48 overflow-hidden rounded-t-lg">
                    <img
                      src={specialty.image || "/placeholder.svg"}
                      alt={specialty.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{specialty.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{specialty.description}</p>
                  </div>

                </CardContent>
              </Card>
            ))}
          </div>

        </div>
      </section>

      {/* Services */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-0 max-w-6xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Services & Procedures</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">Comprehensive anesthesia services for all surgical procedures</p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {treatments.map((treatment, index) => (
              <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg shadow-sm">
                <CheckCircle2 className="h-6 w-6 text-teal-600 flex-shrink-0 mt-1" />
                <span className="text-gray-700 font-medium">{treatment}</span>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Why Choose */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-0 max-w-6xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose MAMS Hospitals for Anaesthesiology</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">Trusted anesthesia excellence with patient-centered care</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChoose.map((item, index) => {
              const Icon = item.icon;
              return (
                <Card key={index} className="border-gray-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">

                    <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                      <Icon className="h-7 w-7 text-teal-600" />
                    </div>

                    <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>

                  </CardContent>
                </Card>
              );
            })}
          </div>

        </div>
      </section>

      {/* MEET SURGEON */}
                {doctors.length > 0 && (
          <section className="py-16 px-0 bg-apple-gray-50">
            <div className="container mx-auto max-w-6xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-semibold text-apple-gray-500 mb-4">Meet Our Specialists</h2>
              </div>
              <div className="relative">
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: -340, behavior: 'smooth' }); }}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                </button>
                <div id="doc-scroll" className="flex gap-6 overflow-x-auto scroll-smooth px-10 scrollbar-hide pb-4">
                  {doctors.map(doctor => (
                    <div key={doctor.id} className="w-[280px] sm:w-[300px] flex-shrink-0">
                      <DoctorCard doctor={{
                        name: doctor.title.rendered,
                        slug: doctor.slug,
                        imageUrl: doctor._embedded?.["wp:featuredmedia"]?.[0]?.source_url || "",
                        specialty: doctor._embedded?.["wp:term"]?.[0]?.[0]?.name || "",
                        designation: doctor.acf?.designation || "",
                        qualification: doctor.acf?.qualification || "",
                        experience: doctor.acf?.experience || "",
                      }} />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => { const el = document.getElementById('doc-scroll'); if(el) el.scrollBy({ left: 340, behavior: 'smooth' }); }}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-md rounded-full p-2 hover:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </button>
              </div>
              <div className="text-center mt-8">
                <Link to="/doctors">
                  <button className="px-6 py-3 rounded-full border text-sm font-medium inline-flex items-center gap-2">
                    View All Doctors
                  </button>
                </Link>
              </div>
            </div>
          </section>
          )}

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-0 max-w-4xl">

          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-lg text-gray-600">Get answers to common questions about anesthesia</p>
          </div>

          <Accordion type="single" collapsible className="bg-white rounded-lg shadow-sm">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>

                <AccordionTrigger className="text-left px-6 hover:text-teal-600">
                  {faq.question}
                </AccordionTrigger>

                <AccordionContent className="px-6 text-gray-600 leading-relaxed">
                  {faq.answer}
                </AccordionContent>

              </AccordionItem>
            ))}
          </Accordion>

        </div>
      </section>

      {/* Contact & Appointment Section */}
      <section className="py-16 px-0 bg-apple-teal text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4">Book an Appointment</h2>
            <p className="text-white/90 max-w-2xl mx-auto">
              Your heart health is our top priority. Book an appointment today with Hyderabad's trusted experts at MAMS Hospitals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Phone className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Call Us</h3>  
                  <a href="tel:+917399999981" className="text-white/90 hover:text-white">
                  +91-7399999981
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <Mail className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Email Us</h3>
                <a href="mailto: info@mamshospitals.com" className="text-white/90 hover:text-white">
                   info@mamshospitals.com
                </a>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 rounded-3xl">
              <CardContent className="p-6 text-center">
                <MapPin className="h-8 w-8 mx-auto mb-4 text-white" />
                <h3 class="font-bold text-white mb-2">Visit Us</h3>
                <p className="text-white/90">Bachupally, Miyapur</p>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            {/* <Link href="/book-appointment">
              <Button size="lg" variant="secondary" className="rounded-full">
                <Calendar className="mr-2 h-5 w-5" />
                Schedule Your Appointment
              </Button>
            </Link> */}
          </div>
        </div>
      </section>

      {/* Emergency Banner */}
      <section className="py-8 px-0 bg-red-600 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8" />
              <div>
                <h3 className="text-xl font-bold">24/7 Emergency Care</h3>
                <p className="text-white/90">Fully equipped emergency department</p>
              </div>
            </div>
            <a href="tel:+917399999981">
              <Button size="lg" variant="secondary" className="w-full sm:w-auto rounded-full bg-white text-red-600 hover:bg-gray-100">
                <Phone className="mr-2 h-5 w-5" />
                Call Emergency
              </Button>
            </a>
          </div>
        </div>
      </section>

    </div>
    </>
  );
}
